/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, MessageSquare, Sparkles } from 'lucide-react';
import { api } from '@/services/api';
import { formatDateTimeAR } from '@/utils/dateFormat';
import { ALL_TEMPLATES } from '@/utils/claimTemplates';
import LoadingIndicator from '@/components/LoadingIndicator';
import { linkify } from '@/utils/linkify';

export default function ClaimChatModal({ claim, currentUserId, userRole, isStaff, language = 'es', onClose, onRead, guestBackdrop = false }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const scrollRef = useRef(null);
  const readRef = useRef(false);

  const canUseTemplates = Boolean(isStaff || userRole === 'admin' || userRole === 'house_manager');

  const markRead = async () => {
    try {
      await api.markClaimRead(claim.id);
      if (!readRef.current) {
        readRef.current = true;
        onRead?.(claim.id);
      }
    } catch {
      /* silent: the badge just stays until the next attempt */
    }
  };

  const load = async () => {
    try {
      const data = await api.getClaimMessages(claim.id);
      setMessages((prev) => {
        if (prev.length !== data.length) return data;
        const lastPrev = prev[prev.length - 1];
        const lastNew = data[data.length - 1];
        if (lastPrev?.id !== lastNew?.id) return data;
        return prev;
      });
      await markRead();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    }
    setLoading((prev) => (prev ? false : prev));
  };

  useEffect(() => {
    load();
    const tick = () => {
      if (document.visibilityState === 'visible') load();
    };
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [claim.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setError('');
    try {
      const msg = await api.postClaimMessage(claim.id, t);
      setMessages((prev) => [...prev, msg]);
      setText('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error');
    }
    setSending(false);
  };

  return createPortal(
    <div className={`fixed inset-0 z-50 modal-backdrop${guestBackdrop ? ' claim-chat-modal-backdrop' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="claim-chat-modal">
      <div
        className="bg-surface border-2 border-foreground rounded-2xl w-full max-w-md modal-card flex flex-col"
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">
              {language === 'es' ? 'Chat del reclamo' : 'Claim chat'}
            </p>
            <h3 className="font-heading font-bold text-base text-foreground truncate">{claim.title}</h3>
          </div>
          <button onClick={onClose} className="p-1 text-foreground" aria-label="Close" data-testid="claim-chat-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="claim-chat-messages">
          {loading ? (
            <LoadingIndicator label={language === 'es' ? 'Cargando…' : 'Loading…'} className="py-6" />
          ) : messages.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
              {language === 'es' ? 'Aún no hay mensajes. Iniciá la conversación.' : 'No messages yet. Start the conversation.'}
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.user_id === currentUserId;
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                    mine
                      ? 'bg-foreground text-background rounded-br-md'
                      : 'bg-muted text-foreground rounded-bl-md border border-border'
                  }`}>
                    {!mine && (
                      <p className="text-[10px] font-semibold opacity-70 mb-0.5">
                        {m.user_role === 'guest' ? m.user_name : (language === 'es' ? 'Administración' : 'Admin')}
                      </p>
                    )}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{linkify(m.text)}</p>
                    <p className={`text-[9px] mt-1 ${mine ? 'opacity-60' : 'text-muted-foreground'}`}>
                      {formatDateTimeAR(m.created_at, language === 'es' ? 'es-AR' : 'en-US')}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {canUseTemplates && (
          <div className="px-4 py-2 border-t border-border bg-muted/20 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowTemplates(!showTemplates)}
                className="text-xs font-semibold text-foreground flex items-center gap-1.5 hover:underline"
                data-testid="toggle-chat-templates"
              >
                <Sparkles className="w-3.5 h-3.5 text-accent-yellow fill-accent-yellow" />
                <span>{language === 'es' ? '⚡ Plantillas / Respuestas Rápidas' : '⚡ Quick Templates'}</span>
              </button>
            </div>
            {showTemplates && (
              <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto p-2 bg-surface border-2 border-foreground rounded-xl" data-testid="chat-template-chips">
                {ALL_TEMPLATES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => {
                      setText(t.text);
                      setShowTemplates(false);
                    }}
                    className="text-[11px] px-2.5 py-1 bg-muted hover:bg-accent-mint border border-foreground rounded-lg font-medium text-foreground text-left transition-colors"
                    data-testid={`chat-template-chip-${t.key}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <form onSubmit={send} className="px-4 py-3 border-t border-border flex items-center gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={language === 'es' ? 'Escribí un mensaje…' : 'Type a message…'}
            className="input-brutal flex-1 px-3 py-2"
            style={{ fontSize: '16px' }}
            data-testid="claim-chat-input"
            disabled={sending}
            maxLength={2000}
            inputMode="text"
            enterKeyHint="send"
            autoComplete="off"
            autoCorrect="on"
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            className="w-10 h-10 rounded-xl bg-foreground text-background flex items-center justify-center disabled:opacity-40"
            data-testid="claim-chat-send"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
        {error && <p className="px-4 pb-3 text-xs text-destructive">{error}</p>}
      </div>
    </div>,
    document.body
  );
}
